<template>
  <div class="project">
    <!-- Title -->

    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
          <img id="navbar-logo" src="../../assets/projects/sparky/sparky.jpg" alt="Sparky Logo">
      </div>
    </nav>
     <!-- images E:\werk\export\delta -->

    <h1>FEEL THE SPARK</h1>
    
    <!-- Introduction -->
    <p>
      By the Delta Group of Fontys ICT - University of Applied Sciences
      "Feel the spark" shows us a creation on the boundary between two worlds, while it is trying to find a connection with the humans passing it by. With its vivid coloured fur and mesmerizing patterns on it, it tries to attract the humans to touch it.
      Different kinds of interaction will trigger different behaviours on this living installation. Just like us, it can be lonely, curious, or overstimulated. The way it responds to touch, movement, and sound depends on the mood it's in.
      Welcome to the world unknown - stretch out your hand and feel the spark!
    </p>
    <br>
    <video class="vid" src="../../assets/projects/sparky/teaser.mp4" muted controls autoplay></video>
    <!-- Summary -->
    <h2>Summary</h2>
    <p>
      Feel The Spark is an interactive light installation created by the Delta team of Fontys Applied University of Applied Sciences.
      Feel The Spark shows us a mysterious creation on the boundary between two worlds, while it is trying to find a connection with the humans passing it by. With its vivid coloured fur and mesmerizing patterns on it, it tries to attract the humans to touch it.
      Welcome to the world unknown - stretch out your hand and feel the spark!
      Project was executed between September - November 2018.
      Installation was on display during the light festival Glow, between 07.11 - 18.11.2018 at Pullman Hotel in Eindhoven, Netherlands.
    </p>
    
    <!-- Project Overview -->
    <h2>Project Overview</h2>
    <p>
      Background
      This project started officially in September 2018, but before that point, the previous semester project group had a project with the outcome of a rudimentary concept and proof-of-concept level of the installation. This concept, called "Bright," was approved by the stakeholder Tom Broumels from Glow-festival organization, meaning that the final installation would be on display during Glow-festival, which was the goal of the previous semester project.
      Original concept of "Bright" was "mysterious creature between digital and natural world trying to find a connection between people." In the PoC, this creature's "fur" was made from optical fibers, and the interaction with the installation was captured with Flex-sensors, which detect possible touch with the fibers. Below each optical fiber object is LED, which was controlled by the lighting software "LightJams". The idea of the concept was that depending on the level of intensity of the interaction, the creature changes color, which represents the "mood" of the creature.
    </p>
    
    <!-- Project Management Approach -->
    <h2>Project Management Approach</h2>
    <p>
      Project was divided into phases on the timeline, and each phase's milestones were mirrored from the high-level project goals. Each phase activities were mirrored to each sprint with more detailed actions, which were documented on Trello cards with corresponding people responsible for it, respecting SCRUM methodology. With the approach of using project phasing with the sprints, project management was able to manage the current state of the project and oversee possible setbacks that might come to achieving the needed next phase of the project in the planned time.
      Project members had weekly meetings, where each weekly sprint was wrapped up and the next one kicked off. Besides that meeting, group members agreed on other meetings and workshop days.
      Building the installation base was a time-consuming process. Working with fiberglass was something none of the project members did before, and there was an "itching" learning curve on how to achieve the desired goal with the structure of the installation. After learning from the mistakes and documenting needed steps on how to work with fiberglass, the team managed to achieve the goal during the Autumn holiday week. Because of underestimated time needed to finish the base of the installation, the team didn't have enough time to make planned improvements at this phase of the project, and this activity had to be moved to the next phase.
    </p>
    
    <!-- Installation Monitoring -->
    <h2>Installation Monitoring</h2>
    <p>
      Because the installation was on 24/7, a solution was needed to monitor the state of the installation. The team identified two major functionalities that should always be working: "LightJam" and "TeamViewer" software. For this, the team created a Windows task monitoring that sent emails to team members if the software is not running. With this solution, there was a possibility to know:
      1. If LightJam crashed, there were no lights in the installation.
      2. If TeamViewer crashed, there was no internet connection.
      For the duty hours, the team had a planned duty roster. During the duty shift, the main task was to explain and help visitors of Glow to interact with the installation. This was also related to the identified KPI "People will understand the purpose of the installation and reach the goal via interaction." Also, members took notes of possible improvements. To support and document these chances, a so-called "Glow diary" document was created, where each day's outcomes were documented. This document includes notes of possible needed improvements and how they were made during the Glow week. Possible needed improvements and actions related to them are outcomes from the feedback from the visitors and observations made by the project members who were in duty during Glow official opening hours. With the notes made, the team made some improvements, mostly related to the sensor accuracy and LED-matrix effects.
    </p>
    
    <!-- Concept -->
    <h2>Concept</h2>
    <p>
      For the creature design, the team went for a worm shape. They chose this because it's nice to construct and gives them a lot of space for changes if needed. The worm is around 3 meters long and 1 meter in width. It's presented on a table that is high enough for kids to also reach the worm and stroke it.
      Optical fiber design: The team needed an easy way to connect the optical fibers to the worm itself, so they came up with this design.
      This design is easy to assemble and is fast to produce.
    </p>
    <img src="../../assets/projects/sparky/3.jpg" alt="Worm Design">
    
    <h2>Laser Cut Designs</h2>
    <p>
      To assemble the worm, the team made these arches. The arches are used to hold the iron net in place so they can make glass fiber in the correct shape. The arches are 80 cm apart, which makes it easier to move the worm from school to the location for Glow.
    </p>
    <img src="../../assets/projects/sparky/s5.jpg" alt="Laser Cut Designs">
    
    <!-- How the Arduino Is Connected to LightJam -->
    <h2>How the Arduino Is Connected to LightJam</h2>
    <p>
      How it used to be connected: The flex sensors used in the Glow project were connected to an ESP32 using a multiplexer that could send an analog value to LightJam using Wi-Fi. That signal would be used to render a DMX signal that would be sent over Wi-Fi to another ESP32 that was connected to the RGB LED strip.
      Why we did that: The reason we replaced the ESP32s using an Arduino Mega is that we expected that the people walking around Glow will all have a mobile phone that could easily jam the Wi-Fi signal. Also, we are using an Ethernet cable, which is more secure and a lot faster to connect.
    </p>
    
    <!-- Diagrams -->
    <h2>Diagrams</h2>
    <div class="container">
    <div class="columns is-multiline">
        <div class="column is-half">
            <p>Old Design:</p>
            <img src="../../assets/projects/sparky/c1.jpg" alt="Old Design">
        </div>
        <div class="column is-half">
            <p>New Design:</p>
            <img src="../../assets/projects/sparky/c2.jpg" alt="New Design">
        </div>
    </div>
</div>
    
    <!-- Final Concept Idea -->
    <h2>Final Concept Idea</h2>
    <p>
      To get the shape right, the team used wood arches connected to an iron net. After getting the shape right, they tried this shape out with the optical fibers in place. They cut these wood arches using a jigsaw.
      The result looks nice and even on every part.
    </p>
    <img src="../../assets/projects/sparky/s5.jpg" alt="Final Concept Idea">
    
    <!-- Making the Glass Fiber -->
    <h2>Making the Glass Fiber</h2>
    <p>
      To make the glass fiber, the team first attached the iron net to the wooden arches to get the right shape. After this, they added a glass fiber sheet and added rosin to harden it. After this, they repeated the same process to make it 3 layers thick and very strong.
    </p>
    <img src="../../assets/projects/sparky/s191.jpg" alt="Making the Glass Fiber">
    
    <!-- Making the First Prototype for Dutch Design Week -->
    <h2>Making the First Prototype for Dutch Design Week</h2>
    <p>
      After getting one piece ready for the prototype, the team had to paint it black, cut it to the right size, and remove the excess glass fiber and add holes to it for the LED lights. We found out that if you don't add enough rosin to harden the piece, it's very difficult to cut and saw through it, so we have to keep that in mind if we make the other parts.
    </p>
    <img src="../../assets/projects/sparky/s19.jpg" alt="First Prototype for Dutch Design Week">
    
    <!-- Assembling the LED Lights and Sensors -->
    <h2>Assembling the LED Lights and Sensors</h2>
    <p>
      We assembled the lights using the LED holders and flex sensors for the first prototype. While doing this, we found out that we have too much space between the middle row of the optical fibers. We adjusted our plan for the cutting and made the prototype ready. The LEDs are connected in a serial connection going from left to right and back connected with terminal blocks.
    </p>

    <br>
    <div class="columns is-multiline">
        <div class="column is-half">
            <strong>Assembling the LED Lights and Sensors:</strong>
            <img src="../../assets/projects/sparky/s8.jpg" alt="Assembling the LED Lights and Sensors">
        </div>
        <div class="column is-half">
            <strong>Result:</strong>
            <img src="../../assets/projects/sparky/s9.jpg" alt="Result">
        </div>
    </div>
    
    <!-- How We Are Going to Connect the Pieces -->
    <h2>How We Are Going to Connect the Pieces</h2>
    <p>
      To connect the pieces, we are using the arches and add angle irons to connect the arches to the piece. The arches have holes in them for screws so we can mount the parts with ease.
    </p>
    <img src="../../assets/projects/sparky/s10.png" alt="Connecting the Pieces">
    
    <!-- End Result -->
    <img src="../../assets/projects/sparky/s11.jpg" alt="End Result">
    
    <!-- Interaction -->
    <h2>Interaction</h2>
    <p>
      "Feel the spark" shows us a creation on the boundary between two worlds, while it is trying to find a connection with the humans passing it by. With its vivid colored fur and mesmerizing patterns on it, 'Sparky' tries to attract the humans to touch it.
      Different kinds of interaction will trigger different behaviors on this living installation. Just like us, it can be lonely, curious, or overstimulated. The way it responds to touch and movement depends on the mood it's in.
      Welcome to the world unknown - stretch out your hand and feel the spark!
    </p>
    
    <!-- Structure -->
    <h2>Structure</h2>
    <p>
      The fur of the creature is made from optic fibers, and it feels like actual fur. The body of the creature is made from hardened glass fiber and has a black top coat to give maximum effect in a dark room.
    </p>
    <div class="container">
    <div class="columns is-multiline">
        <div class="column is-half">
            <img src="../../assets/projects/sparky/GLOW_demo_interaction2.png" alt="Image 1">
        </div>
        <div class="column is-half">
            <img src="../../assets/projects/sparky/GLOW_demo_interaction4.png" alt="Image 2">
        </div>
        <div class="column is-half">
            <img src="../../assets/projects/sparky/GLOW_demo_interaction5.png" alt="Image 3">
        </div>
        <div class="column is-half">
            <img src="../../assets/projects/sparky/GLOW_demo_interaction6.png" alt="Image 4">
        </div>
        <div class="column is-half">
            <img src="../../assets/projects/sparky/GLOW_demo_interaction7.png" alt="Image 5">
        </div>
        <div class="column is-half">
            <img src="../../assets/projects/sparky/GLOW_demo_interaction8.png" alt="Image 6">
        </div>
    </div>
</div>
    <!-- Measurements -->
    <h2>Measurements</h2>
    <p>
      *measurements in mm
      It is desirable that the "free zone" around the installation is about 1m in all sides. This ensures free moving around the installation for the interaction.
    </p>
    <img src="../../assets/projects/sparky/s16.jpg" alt="Measurements">
    <img src="../../assets/projects/sparky/s14.jpg" alt="Measurements">
    
    <!-- 3D Model -->
    <section class="project-section">
      <h2>3D Model</h2>
      <p>
        Following 3D model, pictures do not include the "fur", the optical fibers. Model pictures are created to get a general idea of the size and shape of the installation.
      </p>
      <img src="../../assets/projects/sparky/s18.jpg" alt="3D Model">
    </section>
  </div>
</template>




<style scoped>
  
@import 'project.css';

</style>